import React, { useState, useEffect } from 'react';
import {
    Box,
    Center,
    Flex,
    Grid,
    Text,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    HStack,
    Button,
    Table,
    TableContainer,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    Spinner,
} from '@chakra-ui/react';
import {
    FiUsers,
    FiTrendingUp,
    FiClock,
    FiArrowUpRight,
    FiArrowDownRight,
    FiArrowLeft,
} from 'react-icons/fi';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
} from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';
import Header from '../../components/Header';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../../helpers/axiosInstance';
import { getApiHeaders, hasPermission } from '../../utils/DefaultFunction';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title);

const RFMDashboard = ({ header }) => {
    const navigate = useNavigate();

    const location = useLocation();
    const [featurePermissions, setFeaturePermissions] = useState([]);
    const [featurePermissionsLoading, setFeaturePermissionsLoading] = useState(true);
    // Backend groups RFM permissions under the list page's own feature slug,
    // not this dashboard's route (which includes the model id).
    const featurePermParams = "marketing/rfm-models";

    const fetchFeaturePermissions = async () => {
        setFeaturePermissionsLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${featurePermParams}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            setFeaturePermissions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching permissions:", error);
            setFeaturePermissions([]);
        } finally {
            setFeaturePermissionsLoading(false);
        }
    };
    useEffect(() => {
        fetchFeaturePermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rfmModelDashboardPermission = hasPermission("rfm-model-dashboard", featurePermissions);

    const segmentData = [
        { name: 'Champions', value: 1250, color: '#22c55e', percentage: 25 },
        { name: 'Loyal', value: 1500, color: '#3b82f6', percentage: 30 },
        { name: 'At Risk', value: 1000, color: '#eab308', percentage: 20 },
        { name: 'Lost', value: 750, color: '#ef4444', percentage: 15 },
        { name: 'Others', value: 500, color: '#94a3b8', percentage: 10 },
    ];

    const rfmScoreDistribution = [
        { score: '555', count: 450, segment: 'Champions' },
        { score: '554', count: 380, segment: 'Champions' },
        { score: '545', count: 420, segment: 'Champions' },
        { score: '444', count: 520, segment: 'Loyal' },
        { score: '443', count: 480, segment: 'Loyal' },
        { score: '434', count: 500, segment: 'Loyal' },
        { score: '333', count: 350, segment: 'At Risk' },
        { score: '233', count: 320, segment: 'At Risk' },
    ];

    const trendData = [
        { month: 'Jan', champions: 1100, loyal: 1400, atRisk: 950, lost: 800 },
        { month: 'Feb', champions: 1150, loyal: 1450, atRisk: 980, lost: 770 },
        { month: 'Mar', champions: 1200, loyal: 1480, atRisk: 1020, lost: 760 },
        { month: 'Apr', champions: 1220, loyal: 1500, atRisk: 1000, lost: 750 },
        { month: 'May', champions: 1250, loyal: 1500, atRisk: 1000, lost: 750 },
    ];

    const averageScores = [
        { dimension: 'Recency', score: 3.8, color: '#3b82f6' },
        { dimension: 'Frequency', score: 3.5, color: '#8b5cf6' },
        { dimension: 'Monetary', score: 3.2, color: '#22c55e' },
    ];

    const totalCustomers = segmentData.reduce((acc, s) => acc + s.value, 0);

    // Chart.js data configs
    const pieChartData = {
        labels: segmentData.map(s => s.name),
        datasets: [{
            data: segmentData.map(s => s.value),
            backgroundColor: segmentData.map(s => s.color),
            borderWidth: 2,
            borderColor: '#fff',
        }],
    };
    const pieChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw.toLocaleString()} (${segmentData[ctx.dataIndex].percentage}%)` } },
        },
    };

    const barChartData = {
        labels: averageScores.map(s => s.dimension),
        datasets: [{
            label: 'Average Score',
            data: averageScores.map(s => s.score),
            backgroundColor: averageScores.map(s => s.color),
            borderRadius: 8,
        }],
    };
    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: { min: 0, max: 5, grid: { color: '#f1f5f9' } },
            x: { grid: { display: false } },
        },
        plugins: { legend: { display: false } },
    };

    const lineChartData = {
        labels: trendData.map(d => d.month),
        datasets: [
            { label: 'Champions', data: trendData.map(d => d.champions), borderColor: '#22c55e', backgroundColor: '#22c55e20', tension: 0.3, pointRadius: 4 },
            { label: 'Loyal', data: trendData.map(d => d.loyal), borderColor: '#3b82f6', backgroundColor: '#3b82f620', tension: 0.3, pointRadius: 4 },
            { label: 'At Risk', data: trendData.map(d => d.atRisk), borderColor: '#eab308', backgroundColor: '#eab30820', tension: 0.3, pointRadius: 4 },
            { label: 'Lost', data: trendData.map(d => d.lost), borderColor: '#ef4444', backgroundColor: '#ef444420', tension: 0.3, pointRadius: 4 },
        ],
    };
    const lineChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: { grid: { color: '#f1f5f9' } },
            x: { grid: { display: false } },
        },
        plugins: { legend: { position: 'bottom' } },
    };

    const getSegmentColor = (segment) => {
        switch (segment) {
            case 'Champions': return '#22c55e';
            case 'Loyal': return '#3b82f6';
            case 'At Risk': return '#eab308';
            default: return '#ef4444';
        }
    };

    const getSegmentColorScheme = (segment) => {
        switch (segment) {
            case 'Champions': return 'green';
            case 'Loyal': return 'blue';
            case 'At Risk': return 'yellow';
            default: return 'red';
        }
    };

    if (featurePermissionsLoading) {
        return (
            <>
                <Header header={header} />
                <BoxWrapper>
                    <Center h="400px">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                </BoxWrapper>
            </>
        );
    }

    return (
        <>
            <Header header={header} />
            <BoxWrapper>
              {rfmModelDashboardPermission ? (
                <>
                {/* Back button */}
                <Button leftIcon={<FiArrowLeft />} variant="ghost" size="sm" mb={4} onClick={() => navigate('/marketing/rfm-models')}>
                    Back to Models
                </Button>

                <Text fontSize="2xl" fontWeight={600} mb={1}>RFM Analytics Dashboard</Text>
                <Text color="gray.600" mb={6}>Segment distribution and performance metrics</Text>

                {/* KPI Cards */}
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={6} mb={8}>
                    <Box className="box-style-card">
                        <Stat>
                            <Flex justify="space-between" align="center" mb={2}>
                                <StatLabel>Total Scored Customers</StatLabel>
                                <Box w={10} h={10} bg="green.100" borderRadius="full" display="flex" alignItems="center" justifyContent="center">
                                    <Box color="green.600"><FiUsers /></Box>
                                </Box>
                            </Flex>
                            <StatNumber>{totalCustomers.toLocaleString()}</StatNumber>
                            <StatHelpText>
                                <HStack spacing={1}><Box color="green.500"><FiArrowUpRight size={14} /></Box><Text color="green.500" as="span">12%</Text><Text as="span" color="gray.500">vs last month</Text></HStack>
                            </StatHelpText>
                        </Stat>
                    </Box>

                    <Box className="box-style-card">
                        <Stat>
                            <Flex justify="space-between" align="center" mb={2}>
                                <StatLabel>Champions</StatLabel>
                                <Box w={10} h={10} bg="green.100" borderRadius="full" display="flex" alignItems="center" justifyContent="center">
                                    <Box color="green.600"><FiTrendingUp /></Box>
                                </Box>
                            </Flex>
                            <StatNumber>1,250</StatNumber>
                            <StatHelpText>
                                <HStack spacing={1}><Box color="green.500"><FiArrowUpRight size={14} /></Box><Text color="green.500" as="span">8%</Text><Text as="span" color="gray.500">vs last month</Text></HStack>
                            </StatHelpText>
                        </Stat>
                    </Box>

                    <Box className="box-style-card">
                        <Stat>
                            <Flex justify="space-between" align="center" mb={2}>
                                <StatLabel>Avg Recency Score</StatLabel>
                                <Box w={10} h={10} bg="blue.100" borderRadius="full" display="flex" alignItems="center" justifyContent="center">
                                    <Box color="blue.600"><FiClock /></Box>
                                </Box>
                            </Flex>
                            <StatNumber>3.8 / 5</StatNumber>
                            <StatHelpText>
                                <HStack spacing={1}><Box color="green.500"><FiArrowUpRight size={14} /></Box><Text color="green.500" as="span">5%</Text><Text as="span" color="gray.500">vs last month</Text></HStack>
                            </StatHelpText>
                        </Stat>
                    </Box>

                    <Box className="box-style-card">
                        <Stat>
                            <Flex justify="space-between" align="center" mb={2}>
                                <StatLabel>At Risk Customers</StatLabel>
                                <Box w={10} h={10} bg="yellow.100" borderRadius="full" display="flex" alignItems="center" justifyContent="center">
                                    <Box color="yellow.600"><FiUsers /></Box>
                                </Box>
                            </Flex>
                            <StatNumber>1,000</StatNumber>
                            <StatHelpText>
                                <HStack spacing={1}><Box color="red.500"><FiArrowDownRight size={14} /></Box><Text color="red.500" as="span">3%</Text><Text as="span" color="gray.500">vs last month</Text></HStack>
                            </StatHelpText>
                        </Stat>
                    </Box>
                </Grid>

                {/* Charts Row */}
                <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6} mb={8}>
                    {/* Pie Chart */}
                    <Box className="box-style-card">
                        <Text fontWeight={600} mb={4}>Customer Segment Distribution</Text>
                        <Box h="300px">
                            <Pie data={pieChartData} options={pieChartOptions} />
                        </Box>
                        <Grid templateColumns="repeat(2, 1fr)" gap={3} mt={4}>
                            {segmentData.map(segment => (
                                <HStack key={segment.name} spacing={2}>
                                    <Box w={3} h={3} borderRadius="full" bg={segment.color} flexShrink={0} />
                                    <Text fontSize="sm">{segment.name}</Text>
                                    <Text fontSize="sm" color="gray.500" ml="auto">{segment.value.toLocaleString()}</Text>
                                </HStack>
                            ))}
                        </Grid>
                    </Box>

                    {/* Bar Chart */}
                    <Box className="box-style-card">
                        <Text fontWeight={600} mb={4}>Average RFM Scores</Text>
                        <Box h="300px">
                            <Bar data={barChartData} options={barChartOptions} />
                        </Box>
                        <Grid templateColumns="repeat(3, 1fr)" gap={4} mt={4}>
                            {averageScores.map(item => (
                                <Box key={item.dimension} textAlign="center">
                                    <Box w={8} h={8} borderRadius="full" mx="auto" mb={2} display="flex" alignItems="center" justifyContent="center" style={{ backgroundColor: `${item.color}20` }}>
                                        <Box style={{ color: item.color }}>
                                            {item.dimension === 'Recency' && <FiClock size={14} />}
                                            {item.dimension === 'Frequency' && <FiTrendingUp size={14} />}
                                            {item.dimension === 'Monetary' && <FiUsers size={14} />}
                                        </Box>
                                    </Box>
                                    <Text fontWeight="medium">{item.score}</Text>
                                    <Text fontSize="xs" color="gray.500">{item.dimension}</Text>
                                </Box>
                            ))}
                        </Grid>
                    </Box>
                </Grid>

                {/* Line Chart */}
                <Box className="box-style-card" mb={8}>
                    <Text fontWeight={600} mb={4}>Segment Trends (Last 5 Months)</Text>
                    <Box h="300px">
                        <Line data={lineChartData} options={lineChartOptions} />
                    </Box>
                </Box>

                {/* RFM Score Distribution Table */}
                <Box className="box-style-card">
                    <Text fontWeight={600} mb={4}>RFM Score Distribution</Text>
                    <TableContainer>
                        <Table variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>RFM Score</Th>
                                    <Th>Segment</Th>
                                    <Th isNumeric>Customer Count</Th>
                                    <Th isNumeric>Percentage</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {rfmScoreDistribution.map((item, index) => (
                                    <Tr key={index} _hover={{ bg: 'gray.50' }}>
                                        <Td fontFamily="mono" fontWeight="medium">{item.score}</Td>
                                        <Td>
                                            <HStack spacing={2}>
                                                <Box w={2} h={2} borderRadius="full" bg={getSegmentColor(item.segment)} />
                                                <Badge colorScheme={getSegmentColorScheme(item.segment)}>{item.segment}</Badge>
                                            </HStack>
                                        </Td>
                                        <Td isNumeric>{item.count.toLocaleString()}</Td>
                                        <Td isNumeric color="gray.500">{((item.count / totalCustomers) * 100).toFixed(1)}%</Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </Box>
                </>
              ) : (
                <Center h="400px">
                    <Box textAlign="center">
                        <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                            No Access
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                            You don't have permission to view this sections.
                        </Text>
                    </Box>
                </Center>
              )}
            </BoxWrapper>
        </>
    );
};

export default RFMDashboard;
